import { isCategoryModule } from '../constants';
import type { ModuleOrderEntry } from '../api/types';
import type { VideoCarousel } from '../components/VideoCarousel/VideoCarouselModal';

const CUSTOM_CAROUSEL_PREFIX = 'custom-carousel:';

export function toChannelId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

export function normalizeChannelIds(values: unknown): number[] {
  if (!Array.isArray(values)) return [];

  return Array.from(new Set(values.map(toChannelId).filter((id): id is number => id !== null)));
}

export function getDefaultModuleTitle(
  entry: ModuleOrderEntry,
  categoryLabels: Record<string, string>,
  carousels: VideoCarousel[]
): string {
  if (entry.type === 'category') {
    return categoryLabels[entry.category] ?? entry.category;
  }

  const carousel = carousels.find((item) => item.id === entry.carouselId);
  return carousel?.title ?? 'Custom Carousel';
}

export function getEffectiveModuleTitle(
  entry: ModuleOrderEntry,
  categoryLabels: Record<string, string>,
  carousels: VideoCarousel[]
): string {
  const customTitle = entry.title?.trim();
  if (customTitle) return customTitle;
  return getDefaultModuleTitle(entry, categoryLabels, carousels);
}

function withOptionalTitle<T extends ModuleOrderEntry>(entry: T, title: unknown): T {
  if (typeof title !== 'string' || !title.trim()) {
    return entry;
  }
  return { ...entry, title: title.trim() };
}

export function getModuleKey(entry: ModuleOrderEntry): string {
  if (entry.type === 'custom-carousel') {
    return `${CUSTOM_CAROUSEL_PREFIX}${entry.id}`;
  }
  return entry.category;
}

export function createModuleEntry(
  option: string,
  allChannelIds: number[] = [],
  categoryLabels: Record<string, string> = {}
): ModuleOrderEntry {
  if (option === 'custom-carousel') {
    return {
      type: 'custom-carousel',
      id: crypto.randomUUID(),
      carouselId: '',
      title: 'Custom Carousel',
    };
  }

  return {
    type: 'category',
    category: option,
    channelIds: [...allChannelIds],
    title: categoryLabels[option] ?? option,
  };
}

function normalizeEntry(entry: unknown): ModuleOrderEntry | null {
  if (!entry || typeof entry !== 'object') return null;

  const value = entry as Record<string, unknown>;

  if (value.type === 'custom-carousel') {
    const id = typeof value.id === 'string' ? value.id : crypto.randomUUID();
    return withOptionalTitle(
      {
        type: 'custom-carousel',
        id,
        carouselId: typeof value.carouselId === 'string' ? value.carouselId : '',
      },
      value.title
    );
  }

  if (value.type === 'category' && typeof value.category === 'string') {
    return withOptionalTitle(
      {
        type: 'category',
        category: value.category,
        channelIds: normalizeChannelIds(value.channelIds),
      },
      value.title
    );
  }

  return null;
}

export function normalizeModuleOrder(
  moduleOrder?: unknown,
  legacyCustomCarouselSelections?: Record<string, string>,
  legacyCategoryChannelSelections?: Record<string, string | string[]>
): ModuleOrderEntry[] {
  if (!Array.isArray(moduleOrder)) return [];

  if (moduleOrder.length > 0 && typeof moduleOrder[0] === 'object') {
    return moduleOrder
      .map(normalizeEntry)
      .filter((entry): entry is ModuleOrderEntry => entry !== null);
  }

  return (moduleOrder as string[])
    .map((key): ModuleOrderEntry | null => {
      if (key.startsWith(CUSTOM_CAROUSEL_PREFIX)) {
        return {
          type: 'custom-carousel',
          id: key.slice(CUSTOM_CAROUSEL_PREFIX.length),
          carouselId: legacyCustomCarouselSelections?.[key] ?? '',
        };
      }

      if (!isCategoryModule(key)) return null;

      const channelIds = legacyCategoryChannelSelections?.[key];
      return {
        type: 'category',
        category: key,
        channelIds: normalizeChannelIds(
          Array.isArray(channelIds) ? channelIds : channelIds ? [channelIds] : []
        ),
      };
    })
    .filter((entry): entry is ModuleOrderEntry => entry !== null);
}

export function reorderModuleOrderByKeys(
  keys: string[],
  currentEntries: ModuleOrderEntry[],
  allChannelIds: number[] = [],
  categoryLabels: Record<string, string> = {}
): ModuleOrderEntry[] {
  const entryByKey = new Map(currentEntries.map((entry) => [getModuleKey(entry), entry]));

  return keys.map((key) => {
    const existing = entryByKey.get(key);
    if (existing) return existing;

    if (key.startsWith(CUSTOM_CAROUSEL_PREFIX)) {
      return {
        type: 'custom-carousel',
        id: key.slice(CUSTOM_CAROUSEL_PREFIX.length),
        carouselId: '',
        title: 'Custom Carousel',
      };
    }

    return {
      type: 'category',
      category: key,
      channelIds: [...allChannelIds],
      title: categoryLabels[key] ?? key,
    };
  });
}

export function updateCategoryChannelIds(
  entries: ModuleOrderEntry[],
  moduleKey: string,
  channelIds: number[]
): ModuleOrderEntry[] {
  return entries.map((entry) => {
    if (getModuleKey(entry) !== moduleKey || entry.type !== 'category') return entry;
    return { ...entry, channelIds };
  });
}

export function updateCustomCarouselId(
  entries: ModuleOrderEntry[],
  moduleKey: string,
  carouselId: string,
  carousels: VideoCarousel[] = []
): ModuleOrderEntry[] {
  return entries.map((entry) => {
    if (getModuleKey(entry) !== moduleKey || entry.type !== 'custom-carousel') return entry;

    const previousDefault =
      carousels.find((carousel) => carousel.id === entry.carouselId)?.title ?? 'Custom Carousel';
    const nextDefault =
      carousels.find((carousel) => carousel.id === carouselId)?.title ?? 'Custom Carousel';
    const shouldUpdateTitle =
      !entry.title || entry.title === previousDefault || entry.title === 'Custom Carousel';

    return {
      ...entry,
      carouselId,
      title: shouldUpdateTitle ? nextDefault : entry.title,
    };
  });
}

export function updateModuleTitle(
  entries: ModuleOrderEntry[],
  moduleKey: string,
  title: string
): ModuleOrderEntry[] {
  const trimmedTitle = title.trim();

  return entries.map((entry) => {
    if (getModuleKey(entry) !== moduleKey) return entry;
    return { ...entry, title: trimmedTitle };
  });
}

export function prepareModuleOrderForSave(
  entries: ModuleOrderEntry[],
  categoryLabels: Record<string, string>,
  carousels: VideoCarousel[]
): ModuleOrderEntry[] {
  return entries.map((entry) => {
    if (entry.type === 'category') {
      return {
        ...entry,
        channelIds: normalizeChannelIds(entry.channelIds),
        title: getEffectiveModuleTitle(entry, categoryLabels, carousels),
      };
    }

    return {
      ...entry,
      title: getEffectiveModuleTitle(entry, categoryLabels, carousels),
    };
  });
}

export function sanitizeModuleOrder(
  entries: ModuleOrderEntry[],
  validCarouselIds: Set<string>,
  validChannelIds: Set<number>
): ModuleOrderEntry[] {
  let changed = false;

  const next = entries.map((entry) => {
    if (entry.type === 'custom-carousel') {
      if (!entry.carouselId || validCarouselIds.has(entry.carouselId)) {
        return entry;
      }
      changed = true;
      return { ...entry, carouselId: '' };
    }

    const channelIds = entry.channelIds.filter((id) => validChannelIds.has(id));
    if (channelIds.length === entry.channelIds.length) {
      return entry;
    }
    changed = true;
    return { ...entry, channelIds };
  });

  return changed ? next : entries;
}
